import React from 'react';
import notFoundImage from '../../../assets/fox-not-found.webp';
import { getImageUrl } from '../../../lib/helpers';
import { locationIcon } from './LocationChoiceModal';

const Cart = ({
  cartItems = [],
  locById = {},
  onRemoveFromCart,
  onRemoveProduct,
  onClearCart,
  clientCredit,
  subtotal: subtotalProp,
  discountRate = 0,
  onCheckout,
  onAddQuantity,
  onReduceQuantity,
  onContinueShopping,
  onAddCredit,
  error,
  otherArea = null,
  onChangeLineDiscount,
}) => {
  const rawSubtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const subtotal = subtotalProp !== undefined ? subtotalProp : rawSubtotal;
  // Descuento por línea = descuento socio + descuento manual de la línea (clamp 100%).
  const clamp01 = (n) => Math.min(1, Math.max(0, n));
  let memberDiscountAmount = 0;
  let manualDiscountAmount = 0;
  cartItems.forEach((i) => {
    const lineSub = (Number(i.price) || 0) * (Number(i.quantity) || 0);
    const manualRate = clamp01((Number(i.manual_discount) || 0) / 100);
    const effRate = clamp01(discountRate + manualRate);
    const memberPart = lineSub * Math.min(discountRate, effRate);
    memberDiscountAmount += memberPart;
    manualDiscountAmount += lineSub * effRate - memberPart;
  });
  const discountAmount = memberDiscountAmount + manualDiscountAmount;
  const total = Math.max(0, subtotal - discountAmount);
  const clientCreditValue = Number(clientCredit) || 0;
  const insufficientCredit = total > clientCreditValue;
  // Se permite cobrar aunque el saldo quede negativo (el socio queda debiendo).
  // Solo bloquea el carrito vacío.
  const canCheckout = cartItems.length > 0;
  const hasDiscount = discountRate > 0 && memberDiscountAmount > 0;
  const hasManualDiscount = manualDiscountAmount > 0;

  // Items del otro área (solo lectura): el carrito muestra "todo" (barra + dispensario).
  const otherItems = otherArea?.items || [];
  const otherLabel = otherArea?.cartType === 'dispensario' ? 'Dispensario' : 'Barra / Productos';
  const otherSubtotal = otherItems.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <div className="flex flex-col">
      {cartItems.length === 0 && otherItems.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#737373]">Tu carrito está vacío.</p>
      ) : (
        <>
          {cartItems.length > 0 && (
          <>
          {/* Cabecera de columnas */}
          <div className="flex items-center justify-between border-b border-[#E5E5E5] pb-2 text-[11px] font-semibold uppercase tracking-wide text-[#737373]">
            <span className="flex-1">Producto</span>
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className="w-[96px] text-center">Cantidad</span>
              <span className="w-16 text-right">Precio</span>
            </div>
          </div>

          <ul className="divide-y divide-[#E5E5E5]">
            {cartItems.map((item, idx) => (
              <li key={idx} className="flex items-center justify-between gap-2 py-2.5">
                {/* Producto */}
                <div className="flex items-center gap-2 flex-1 min-w-0 group relative">
                  <div className="relative flex-shrink-0">
                    <img
                      src={item.image_path ? getImageUrl(item.image_path) : notFoundImage}
                      alt={item.name}
                      className="w-9 h-9 object-cover rounded-md"
                      onError={(e) => { e.target.src = notFoundImage; }}
                    />
                    {/* Eliminar producto (X al hover) */}
                    {onRemoveProduct && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemoveProduct(item); }}
                        className="absolute -top-1 -right-1 bg-[#DC2626] hover:bg-red-700 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10 text-[10px] font-bold"
                        title="Eliminar producto"
                        type="button"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-[#0A0A0A]">{item.name}</div>
                    <div className="text-xs text-[#737373]">
                      €{Number(item.price).toFixed(2)} / {item.unit_type === 'weight' ? 'g' : 'u'}
                    </div>
                    {/* Descuento manual de la línea (%): se suma al del socio. 100% = regalar. */}
                    {onChangeLineDiscount && (
                      <label className="mt-0.5 flex items-center gap-1 text-[11px] text-[#737373]">
                        <span>Dto.</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={item.manual_discount ?? ''}
                          onChange={(e) => onChangeLineDiscount(item, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="0"
                          className="w-12 rounded border border-[#E5E5E5] px-1 py-0.5 text-right tabular-nums focus:border-lime-400 focus:outline-none"
                        />
                        <span>%</span>
                      </label>
                    )}
                    {/* Regla de oro (Fase D): de qué ubicación se descuenta esta línea */}
                    {item.location_id && locById[String(item.location_id)] && (
                      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[#737373]">
                        <span aria-hidden>{locationIcon(locById[String(item.location_id)].kind)}</span>
                        <span className="truncate">desde {locById[String(item.location_id)].name}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Cantidad + Precio */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  {item.unit_type === 'weight' ? (
                    // Productos por gramo: el peso/báscula se fija al añadir (no se ajusta
                    // aquí) para no descuadrar el stock/merma. Solo lectura; para cambiarlo
                    // se elimina y se vuelve a añadir. Bajo los g cobrados se muestra el
                    // peso real de la báscula (si se introdujo).
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="inline-flex items-center rounded-md border border-[#E5E5E5] px-3 py-1 text-sm tabular-nums min-w-[64px] justify-center">
                        {item.quantity}
                        <small className="ml-0.5 text-[#737373]">g</small>
                      </span>
                      {item.real_weight != null && Number(item.real_weight) > 0 && (
                        <span className="text-[10px] text-[#737373] tabular-nums">báscula {parseFloat(Number(item.real_weight).toFixed(3)).toString()}g</span>
                      )}
                    </div>
                  ) : (
                    <div className="inline-flex items-center rounded-md border border-[#E5E5E5]">
                      <button
                        onClick={() => onReduceQuantity(item, 1)}
                        className="px-2 py-1 text-[#737373] hover:bg-[#F5F5F5] transition-colors"
                        type="button"
                      >
                        –
                      </button>
                      <span className="px-1 text-sm tabular-nums min-w-[48px] text-center">
                        {item.quantity}
                        <small className="ml-0.5 text-[#737373]">u</small>
                      </span>
                      <button
                        onClick={() => onAddQuantity(item, 1)}
                        className="px-2 py-1 text-[#737373] hover:bg-[#F5F5F5] transition-colors"
                        type="button"
                      >
                        +
                      </button>
                    </div>
                  )}
                  <div className="w-16 text-right text-sm font-semibold text-[#0A0A0A] whitespace-nowrap">
                    €{(item.price * item.quantity).toFixed(2)}
                  </div>
                  {/* Borrar línea: por gramo no tiene +/- para bajar a 0, así que
                      damos un icono visible y no hay que vaciar el carrito entero. */}
                  {item.unit_type === 'weight' && onRemoveProduct && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onRemoveProduct(item); }}
                      className="flex-shrink-0 text-[#DC2626] hover:text-red-700 p-1 rounded transition-colors"
                      title="Eliminar línea"
                      aria-label="Eliminar línea"
                      type="button"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {/* Totales */}
          <div className="mt-3 border-t border-[#E5E5E5] pt-3 space-y-1">
            <div className="flex justify-between text-sm text-[#737373]">
              <span>Subtotal ({cartItems.length} {cartItems.length === 1 ? 'producto' : 'productos'})</span>
              <span>€{subtotal.toFixed(2)}</span>
            </div>
            {hasDiscount && (
              <div className="flex justify-between text-sm text-[#4D7C0F]">
                <span>Descuento ({(discountRate * 100).toFixed(0)}%)</span>
                <span>-€{memberDiscountAmount.toFixed(2)}</span>
              </div>
            )}
            {hasManualDiscount && (
              <div className="flex justify-between text-sm text-[#4D7C0F]">
                <span>Descuento manual</span>
                <span>-€{manualDiscountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-[#737373]">
              <span>Saldo disponible</span>
              <span className={`font-medium ${insufficientCredit ? 'text-[#DC2626]' : 'text-[#0A0A0A]'}`}>
                €{clientCreditValue.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between border-t border-[#E5E5E5] pt-2 mt-1">
              <span className="text-sm font-semibold text-[#0A0A0A]">Total</span>
              <span className="text-sm font-bold text-[#0A0A0A]">€{total.toFixed(2)}</span>
            </div>
          </div>

          {/* Saldo insuficiente: se PERMITE cobrar; el socio queda en negativo. Aviso + Añadir crédito. */}
          {insufficientCredit && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <div className="flex items-start gap-2 text-xs text-amber-800">
                <span aria-hidden>⚠️</span>
                <div>
                  <div className="font-semibold">El socio quedará en negativo: −€{(total - clientCreditValue).toFixed(2)}</div>
                  <div className="mt-0.5">Puedes cobrar igualmente; la deuda queda pendiente hasta que recargue.</div>
                </div>
              </div>
              {onAddCredit && (
                <button
                  type="button"
                  onClick={onAddCredit}
                  className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-400 hover:bg-amber-500 text-amber-950 text-xs font-semibold px-3 py-1.5 transition-colors"
                >
                  + Añadir crédito al socio
                </button>
              )}
            </div>
          )}

          {error && <p className="mt-2 text-xs text-[#DC2626]">{error}</p>}

          {/* Validar compra */}
          <button
            onClick={() => onCheckout()}
            disabled={!canCheckout}
            className={`mt-3 w-full rounded-md py-2.5 text-sm font-semibold transition-colors ${
              canCheckout
                ? 'bg-[#171717] hover:bg-black text-white cursor-pointer'
                : 'bg-[#F5F5F5] text-[#A3A3A3] cursor-not-allowed'
            }`}
            title={!canCheckout ? 'Carrito vacío' : (insufficientCredit ? 'El socio quedará en negativo' : 'Procesar compra')}
          >
            Validar compra
          </button>

          {/* Seguir añadiendo / Vaciar carrito */}
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onContinueShopping}
              className="inline-flex items-center gap-1 text-sm font-medium text-[#0A0A0A] hover:text-black transition-colors"
            >
              ← Seguir añadiendo
            </button>
            {onClearCart && (
              <button
                type="button"
                onClick={onClearCart}
                className="text-sm font-medium text-[#DC2626] hover:text-red-700 transition-colors"
              >
                Vaciar carrito
              </button>
            )}
          </div>
          </>
          )}

          {/* Otro área (solo lectura): el carrito muestra TODO lo del socio.
              La barra y el dispensario se cobran por separado, así que aquí solo
              se informa; para editar/validar, cambia de área. */}
          {otherItems.length > 0 && (
            <div className={cartItems.length > 0 ? "mt-5 border-t border-[#E5E5E5] pt-4" : ""}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#737373]">
                  También en {otherLabel}
                </span>
                <span className="text-xs font-medium text-[#737373]">€{otherSubtotal.toFixed(2)}</span>
              </div>
              <ul className="divide-y divide-[#E5E5E5] opacity-90">
                {otherItems.map((item, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-2 py-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <img
                        src={item.image_path ? getImageUrl(item.image_path) : notFoundImage}
                        alt={item.name}
                        className="w-8 h-8 object-cover rounded-md flex-shrink-0"
                        onError={(e) => { e.target.src = notFoundImage; }}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-[#0A0A0A]">{item.name}</div>
                        <div className="text-[11px] text-[#737373]">
                          {item.quantity}{item.unit_type === 'weight' ? 'g' : 'u'} · €{Number(item.price).toFixed(2)}/{item.unit_type === 'weight' ? 'g' : 'u'}
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-[#0A0A0A] whitespace-nowrap">
                      €{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] text-[#A3A3A3]">
                Cambia a «{otherLabel}» para editar o validar estos productos.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Cart;
